/**
 * Shared HIBP breach counting — single source of truth for Severity bar + HibpCard.
 */

/**
 * @param {object} res
 * @returns {string}
 */
export function hibpBreachDisplayName(res) {
  return (
    res?.breach_name ||
    res?.details?.title ||
    res?.details?.name ||
    res?.platform ||
    res?.title ||
    'Breach'
  );
}

/**
 * @param {object} row
 * @returns {boolean}
 */
export function isValidHibpBreach(row) {
  if (!row || row.error) return false;
  const name = String(
    row.breach_name || row.details?.name || row.details?.title || '',
  ).trim();
  if (!name || /^unknown(\s+breach)?$/i.test(name)) return false;
  const misc = String(row.details?.misc || row.misc_info || '');
  if (/Breach:/i.test(misc)) return true;
  // Name-only rows are valid: HIBP may truncate to ``{Name}`` without BreachDate.
  return true;
}

/**
 * Normalize breach list nested under hibp_status payloads.
 *
 * @param {object | null | undefined} status
 * @returns {object[]}
 */
export function breachesFromStatus(status) {
  if (!status || typeof status !== 'object') return [];
  const details = status.details && typeof status.details === 'object' ? status.details : {};
  const nested =
    details.hibp_inspect && typeof details.hibp_inspect === 'object'
      ? details.hibp_inspect
      : null;
  const raw = nested?.breaches || details.breaches || [];
  if (!Array.isArray(raw)) return [];
  return raw.map((breach) => ({
    breach_name: breach.breach_name || breach.name || breach.title || breach.Name || breach.Title,
    breach_year:
      breach.breach_year ||
      String(breach.breach_date || breach.date || breach.BreachDate || '').slice(0, 4) ||
      null,
    details: {
      name: breach.name || breach.breach_name || breach.Name,
      title: breach.title || breach.breach_name || breach.Title || breach.Name,
      date: breach.breach_date || breach.date || breach.BreachDate,
      dataclasses: breach.data_classes || breach.dataclasses || [],
      description: breach.description,
    },
  }));
}

/**
 * Deduped, validated breach rows from stream results + status payload.
 *
 * @param {object[]} realResults
 * @param {{ status?: object | null, breaches?: object[] }} [opts]
 * @returns {object[]}
 */
export function collectConfirmedHibpBreaches(realResults, opts = {}) {
  const list = Array.isArray(realResults) ? realResults : [];
  const status =
    opts.status ||
    list.find((r) => r?.ui_bucket === 'hibp_status' || r?.source === 'hibp_status') ||
    null;
  const streamBreaches =
    Array.isArray(opts.breaches) && opts.breaches.length > 0
      ? opts.breaches
      : list.filter(
          (r) =>
            r &&
            (r.source === 'hibp' ||
              r.ui_bucket === 'corporate_breaches' ||
              r.ui_bucket === 'hibp'),
        );
  const statusBreaches = breachesFromStatus(status);
  return [...streamBreaches, ...statusBreaches]
    .filter(isValidHibpBreach)
    .filter((row, idx, arr) => {
      const key = hibpBreachDisplayName(row);
      return arr.findIndex((x) => hibpBreachDisplayName(x) === key) === idx;
    });
}

/**
 * Phone Lookup often emits count metadata without individual hibp rows.
 *
 * @param {object[]} realResults
 * @returns {number}
 */
function resolvePhoneHibpBreachCount(realResults) {
  let fromDetails = 0;
  for (const r of realResults || []) {
    if (
      r?.source !== 'phone_metadata' &&
      r?.source !== 'phone_identity' &&
      r?.source !== 'hibp_status'
    ) {
      continue;
    }
    const n = Number(r?.details?.hibp_breach_count ?? r?.hibp_breach_count ?? 0);
    if (Number.isFinite(n) && n > fromDetails) fromDetails = n;
  }
  const status = (realResults || []).find(
    (r) => r?.source === 'hibp_status' && r?.status === 'breached',
  );
  if (status) {
    const msgMatch = String(status.message || '').match(/(\d+)\s+correlated/i);
    const fromMessage = msgMatch ? Number(msgMatch[1]) : 0;
    return Math.max(fromDetails, fromMessage, 1);
  }
  return fromDetails;
}

/**
 * Canonical breach count for Severity bar + HIBP card.
 *
 * @param {object[]} realResults
 * @param {{ status?: object | null, breaches?: object[] }} [opts]
 * @returns {number}
 */
export function resolveHibpBreachCount(realResults, opts = {}) {
  const list = Array.isArray(realResults) ? realResults : [];
  const status =
    opts.status ||
    list.find((r) => r?.ui_bucket === 'hibp_status' || r?.source === 'hibp_status') ||
    null;
  if (status?.status === 'unavailable' || Boolean(status?.api_error)) {
    return 0;
  }
  const confirmed = collectConfirmedHibpBreaches(list, { ...opts, status });
  if (confirmed.length > 0) return confirmed.length;

  // Authoritative email status: trust breaches_count / nested list when marked breached
  // (HIBP truncateResponse can omit dates; older clients dropped those rows).
  if (status?.status === 'breached') {
    const nested = breachesFromStatus(status);
    const fromStatus = Number(
      status?.breaches_count ?? status?.details?.hibp_inspect?.breaches_count ?? 0,
    );
    const authoritative = Math.max(
      nested.length,
      Number.isFinite(fromStatus) ? fromStatus : 0,
    );
    if (authoritative > 0) return authoritative;
  }

  // Phone Lookup often emits count metadata without individual hibp rows.
  const hasPhoneMeta = list.some(
    (r) => r?.source === 'phone_metadata' || r?.source === 'phone_identity',
  );
  if (hasPhoneMeta) return resolvePhoneHibpBreachCount(list);

  return 0;
}

/**
 * Severity-bar label (null when clean — do not render red badge).
 *
 * @param {number} count
 * @returns {string | null}
 */
export function formatBreachExposureLabel(count) {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  return n === 1 ? 'Exposed in 1 Data Breach' : `Exposed in ${n} Data Breaches`;
}

/**
 * HIBP card risk badge label.
 *
 * @param {number} count
 * @param {{ unavailable?: boolean, statusLabel?: string }} [opts]
 * @returns {string}
 */
export function hibpRiskBadgeLabel(count, opts = {}) {
  if (opts.unavailable) {
    return opts.statusLabel || 'HIBP API UNAVAILABLE';
  }
  const n = Number(count) || 0;
  if (n <= 0) return 'CLEAN (0 Breaches)';
  const noun = n === 1 ? 'Breach' : 'Breaches';
  if (n <= 2) return `MEDIUM RISK (${n} ${noun})`;
  return `CRITICAL RISK (${n} ${noun})`;
}
